import { Component, type ErrorInfo, type ReactNode } from 'react';
import { Alert, Button, Code, Group, Stack, Text } from '@mantine/core';
import { IconAlertTriangle } from '@tabler/icons-react';

/**
 * Ошибка на одном экране не должна уносить всё приложение.
 *
 * React снимает с экрана ВСЁ дерево, если ни один предок не поймал
 * исключение: одна страница с обращением к полю, которого нет в ответе,
 * даёт белый лист вместо программы. Поймали ровно это на гарантийном
 * талоне — страница читала список монтажей, которого в ответе сделки нет.
 *
 * На планшете белый экран хуже всего: замерщик стоит в чужой квартире,
 * рассказать может только «всё пропало», и по такому описанию причину не
 * найти. Здесь он видит название экрана, текст ошибки и кнопку «Назад» —
 * и остальная программа продолжает работать.
 *
 * Текст ошибки показывается КАК ЕСТЬ, по-английски. Перевести его нечем,
 * а спрятать значит лишить единственной зацепки: человек перепишет его в
 * сообщение, и по нему видно, где искать.
 */

interface Props {
  children: ReactNode;
  /** Сбрасывать состояние при переходе — иначе экран остаётся сломанным навсегда */
  resetKey?: string;
}

interface State {
  error: Error | null;
  resetKey?: string;
}

export class PageErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  // Переход на другой адрес обязан снимать ошибку: без этого одна
  // сломанная страница превращает в белый лист и все следующие
  static getDerivedStateFromProps(props: Props, state: State): Partial<State> | null {
    if (state.resetKey !== props.resetKey) {
      return { error: null, resetKey: props.resetKey };
    }
    return null;
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // В консоль — со стеком: она переживает перерисовку, а на экране
    // стек занял бы всё место и напугал бы человека
    console.error('Ошибка на экране', error, info.componentStack);
  }

  render() {
    if (!this.state.error) return this.props.children;

    return (
      <Alert color="red" icon={<IconAlertTriangle size={20} />} title="Экран не открылся">
        <Stack gap="sm">
          <Text size="sm">
            Остальная программа работает — вернитесь назад и продолжайте. Если это
            повторяется, покажите текст ниже тому, кто занимается программой.
          </Text>
          <Code block>{this.state.error.message}</Code>
          <Group gap="sm">
            <Button size="xs" onClick={() => window.history.back()}>
              Назад
            </Button>
            <Button size="xs" variant="subtle" onClick={() => window.location.reload()}>
              Обновить страницу
            </Button>
          </Group>
        </Stack>
      </Alert>
    );
  }
}
